import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import './LegalPage.css';
import './FeedbackPage.css';

type Lang = 'en' | 'nl' | 'ar';
type FeedbackType = 'bug' | 'feature' | 'improvement';

const TEXTS: Record<Lang, {
  title: string;
  back: string;
  subtitle: string;
  typeLabel: string;
  typeBug: string;
  typeFeature: string;
  typeImprovement: string;
  titleLabel: string;
  titlePlaceholder: string;
  descLabel: string;
  descPlaceholder: string;
  submit: string;
  submitting: string;
  successTitle: string;
  successBody: string;
  successBack: string;
  errorGeneric: string;
  required: string;
}> = {
  en: {
    title: 'Feedback',
    back: '← Back',
    subtitle: 'Report a bug or share an idea — we read everything.',
    typeLabel: 'Type',
    typeBug: 'Bug',
    typeFeature: 'Feature Request',
    typeImprovement: 'Improvement',
    titleLabel: 'Title',
    titlePlaceholder: 'Short summary...',
    descLabel: 'Description',
    descPlaceholder: 'Describe the issue or idea in detail...',
    submit: 'Send Feedback',
    submitting: 'Sending...',
    successTitle: 'Thanks for your feedback!',
    successBody: "We've received your message and will review it shortly.",
    successBack: 'Back to settings',
    errorGeneric: 'Something went wrong. Please try again.',
    required: 'This field is required.',
  },
  nl: {
    title: 'Feedback',
    back: '← Terug',
    subtitle: 'Meld een bug of deel een idee — we lezen alles.',
    typeLabel: 'Type',
    typeBug: 'Bug',
    typeFeature: 'Functieverzoek',
    typeImprovement: 'Verbetering',
    titleLabel: 'Titel',
    titlePlaceholder: 'Korte samenvatting...',
    descLabel: 'Beschrijving',
    descPlaceholder: 'Beschrijf het probleem of idee in detail...',
    submit: 'Feedback versturen',
    submitting: 'Verzenden...',
    successTitle: 'Bedankt voor je feedback!',
    successBody: 'We hebben je bericht ontvangen en zullen het snel bekijken.',
    successBack: 'Terug naar instellingen',
    errorGeneric: 'Er is iets misgegaan. Probeer het opnieuw.',
    required: 'Dit veld is verplicht.',
  },
  ar: {
    title: 'ملاحظات',
    back: '← رجوع',
    subtitle: 'أبلغ عن خطأ أو شارك فكرة — نقرأ كل شيء.',
    typeLabel: 'النوع',
    typeBug: 'خطأ',
    typeFeature: 'طلب ميزة',
    typeImprovement: 'تحسين',
    titleLabel: 'العنوان',
    titlePlaceholder: 'ملخص قصير...',
    descLabel: 'الوصف',
    descPlaceholder: 'صف المشكلة أو الفكرة بالتفصيل...',
    submit: 'إرسال الملاحظات',
    submitting: 'جارٍ الإرسال...',
    successTitle: 'شكراً على ملاحظاتك!',
    successBody: 'لقد استلمنا رسالتك وسنراجعها قريباً.',
    successBack: 'العودة إلى الإعدادات',
    errorGeneric: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    required: 'هذا الحقل مطلوب.',
  },
};

export default function FeedbackPage() {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const lang: Lang = currentLanguage === 'nl' || currentLanguage === 'ar' ? currentLanguage : 'en';
  const t = TEXTS[lang];
  const isRtl = lang === 'ar';

  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [descError, setDescError] = useState('');

  const getFeedbackEndpoint = () => {
    const env = (typeof process !== 'undefined' ? (process as any).env : {}) || {};
    const apiUrl = env.REACT_APP_API_URL || env.VITE_API_URL || '/api';
    const apiBase = apiUrl.includes('://')
      ? (apiUrl.endsWith('/api') ? apiUrl : `${apiUrl.replace(/\/$/, '')}/api`)
      : apiUrl;
    return `${apiBase}/feedback`;
  };

  const validate = () => {
    let valid = true;
    if (!title.trim()) { setTitleError(t.required); valid = false; } else setTitleError('');
    if (!description.trim()) { setDescError(t.required); valid = false; } else setDescError('');
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(getFeedbackEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, title: title.trim(), description: description.trim() }),
      });

      if (!res.ok) throw new Error('Failed');
      setSubmitted(true);
    } catch {
      setError(t.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="legal-page" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="legal-page-shell">
        <header className="legal-page-header">
          <div className="legal-page-header-left">
            <button className="legal-page-back" onClick={() => navigate(-1)}>{t.back}</button>
            <span className="legal-page-title">{t.title}</span>
          </div>
        </header>

        {submitted ? (
          <div className="feedback-success">
            <div className="feedback-success-icon">✓</div>
            <h2 className="feedback-success-title">{t.successTitle}</h2>
            <p className="feedback-success-body">{t.successBody}</p>
            <button className="feedback-submit-btn" onClick={() => navigate(-1)}>
              {t.successBack}
            </button>
          </div>
        ) : (
          <form className="feedback-form" onSubmit={handleSubmit} noValidate>
            <p className="feedback-subtitle">{t.subtitle}</p>

            <div className="feedback-type-group">
              <span className="feedback-label">{t.typeLabel}</span>
              <div className="feedback-type-pills">
                {(['bug', 'feature', 'improvement'] as FeedbackType[]).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`feedback-type-pill${type === opt ? ' active' : ''} feedback-type-${opt}`}
                    onClick={() => setType(opt)}
                  >
                    {opt === 'bug' ? t.typeBug : opt === 'feature' ? t.typeFeature : t.typeImprovement}
                  </button>
                ))}
              </div>
            </div>

            <div className="feedback-field">
              <label className="feedback-label" htmlFor="fb-title">{t.titleLabel}</label>
              <input
                id="fb-title"
                className={`feedback-input${titleError ? ' error' : ''}`}
                type="text"
                value={title}
                onChange={e => { setTitle(e.target.value); if (titleError) setTitleError(''); }}
                placeholder={t.titlePlaceholder}
                maxLength={120}
              />
              {titleError && <span className="feedback-error">{titleError}</span>}
            </div>

            <div className="feedback-field">
              <label className="feedback-label" htmlFor="fb-desc">{t.descLabel}</label>
              <textarea
                id="fb-desc"
                className={`feedback-textarea${descError ? ' error' : ''}`}
                value={description}
                onChange={e => { setDescription(e.target.value); if (descError) setDescError(''); }}
                placeholder={t.descPlaceholder}
                rows={5}
                maxLength={1000}
              />
              {descError && <span className="feedback-error">{descError}</span>}
            </div>

            {error && <p className="feedback-error-banner">{error}</p>}

            <button type="submit" className="feedback-submit-btn" disabled={submitting}>
              {submitting ? t.submitting : t.submit}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
